import { FC, useState } from 'react';
import { styled } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import IconDoc from 'src/assets/images/icons/document-text-line.svg';
import IconSend from 'src/assets/images/icons/send-alt.svg';
import IconConfig from 'src/assets/images/icons/setting-4.svg';
import IconPlay from 'src/assets/images/icons/play.svg';
import Receipt from 'src/assets/images/icons/receipt-item-alt.svg';
import IconDocCode from 'src/assets/images/icons/document-code.svg';
import IconWarning from 'src/assets/images/icons/warning-dispute.svg';
import IconTrash from 'src/assets/images/icons/trash.svg';
import { useGetFileUrl, useResolveFileResolution } from 'src/use-cases/internalDashboard/useFileResolution';

import { useDeleteInvoices } from 'src/use-cases/invoice/useDeleteInvoice';
import { store } from 'src/infra/stores/store';
import { DELETE_MODE_KEY } from 'src/infra/stores/reducer/deleteModeSlice';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';
import EyeIcon from '../../../../assets/images/icons/eye.svg';
import { useInternalDashboardSelector } from './Selector';
import { IRowActionMenuProps, ROW_ACTION_TYPE } from './types';

const StyledMenu = styled(Menu)({
  zIndex: '989',

  '& .MuiMenu-paper': {
    borderRadius: '8px!important',
    width: '220px',
  },
});

export const RowActionMenus: FC<IRowActionMenuProps> = (props) => {
  const { data } = props;

  const [actionMenusEl, setActionMenusEl] = useState<HTMLButtonElement | null>(null);

  const { filterParams, isActionLoading, runRowAction, setIsActionLoading } = useInternalDashboardSelector();

  const { refetch: getFileUrl } = useGetFileUrl(data?.invoiceFileProcessingId);
  const { mutateAsync: resolve } = useResolveFileResolution();

  const onClickTrigger = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionMenusEl(event.currentTarget);
  };

  const onCloseActionMenus = () => {
    setActionMenusEl(null);
  };

  const onStartPreview = async () => {
    setIsActionLoading(true);
    await resolve({
      id: data?.id,
      data: {
        status: 'UNDER_REVIEW',
        resolutionType: data?.resolutionType,
      },
      params: filterParams,
    });
    setIsActionLoading(false);
  };

  const onSeeInvoice = async () => {
    const baseUrl = `/action-details/${data?.invoiceId}`;
    const params = new URLSearchParams({ ref: 'internal' });

    if (data?.shipmentInvoiceId) {
      params.append('shipment', data.shipmentInvoiceId);
    }

    const url = `${baseUrl}?${params.toString()}`;
    window.open(url, '_blank');
  };

  const onViewFile = async () => {
    onCloseActionMenus();
    const result = await getFileUrl();
    if (result?.data) {
      window.open(result?.data, '_blank');
    }
  };

  const allowedStatuses = ['REVIEW_REQUIRED', 'NOT_AN_INVOICE', 'MISSING_REQUIRED_FIELD', 'FIXED'];

  const { mutateAsync: deleteFile } = useDeleteInvoices();

  const state = store.getState();
  const enableDeleteMode = state[DELETE_MODE_KEY]?.enable ?? false;

  const handleDelete = (fileName: string) => {
    deleteFile({ fileNames: [fileName] })
      .then(() => {
        openToast({
          title: 'File deleted successfully',
          description: `File ${fileName} has been deleted.`,
          toastType: 'success',
        });
        onCloseActionMenus();
      })
      .catch((error) => {
        openToastError(new Error('Failed to delete invoice'));
      });
  };

  return (
    <>
      <button type='button' onClick={onClickTrigger} aria-label='More Action'>
        <MoreHorizIcon />
      </button>

      <StyledMenu
        open={!!actionMenusEl}
        anchorEl={actionMenusEl}
        onClose={onCloseActionMenus}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem disabled={data?.resolutionStatus !== 'REVIEW_REQUIRED' || isActionLoading} onClick={onStartPreview}>
          <ListItemIcon>
            <img src={IconPlay} alt='' className='w-24' />
          </ListItemIcon>
          <ListItemText>Start Review</ListItemText>
        </MenuItem>
        <MenuItem onClick={onViewFile}>
          <ListItemIcon>
            <img src={Receipt} alt='' className='w-24' />
          </ListItemIcon>
          <ListItemText>View File</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={allowedStatuses.includes(data?.resolutionStatus) || isActionLoading}
          onClick={() => runRowAction(data, ROW_ACTION_TYPE.EDIT_CONFIG)}
        >
          <ListItemIcon>
            <img src={IconConfig} alt='' className='w-24' />
          </ListItemIcon>
          <ListItemText>Edit Config</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={allowedStatuses.includes(data?.resolutionStatus) || isActionLoading}
          onClick={() => runRowAction(data, ROW_ACTION_TYPE.EDIT_INVOICE)}
        >
          <ListItemIcon>
            <img src={IconDoc} alt='' className='w-24' />
          </ListItemIcon>
          <ListItemText>Edit Invoice</ListItemText>
        </MenuItem>
        <MenuItem disabled={allowedStatuses.includes(data?.resolutionStatus) || isActionLoading} onClick={() => runRowAction(data, ROW_ACTION_TYPE.EDIT_OCR)}>
          <ListItemIcon>
            <img src={IconDocCode} alt='' className='w-24' />
          </ListItemIcon>
          <ListItemText>Edit OCR</ListItemText>
        </MenuItem>
        {data?.invoiceId && (
          <MenuItem disabled={isActionLoading} onClick={onSeeInvoice}>
            <ListItemIcon>
              <img src={EyeIcon} alt='' className='w-24' />
            </ListItemIcon>
            <ListItemText>See Invoice</ListItemText>
          </MenuItem>
        )}
        <MenuItem disabled={allowedStatuses.includes(data?.resolutionStatus) || isActionLoading} onClick={() => runRowAction(data, ROW_ACTION_TYPE.RESOLVE)}>
          <ListItemIcon>
            <img src={IconSend} alt='' className='w-24' />
          </ListItemIcon>
          <ListItemText>Resolve</ListItemText>
        </MenuItem>
        <MenuItem disabled={isActionLoading || !data?.invoiceId} onClick={() => runRowAction(data, ROW_ACTION_TYPE.REPROCESS_SHIPMENT)}>
          <ListItemIcon>
            <img src={IconSend} alt='' className='w-24' />
          </ListItemIcon>
          <ListItemText>Reprocess Shipment</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={allowedStatuses.includes(data?.resolutionStatus) || isActionLoading}
          onClick={() => runRowAction(data, ROW_ACTION_TYPE.VALIDATE_INVOICE)}
        >
          <ListItemIcon>
            <img src={IconWarning} alt='' className='w-24' />
          </ListItemIcon>
          <ListItemText>Validate Invoice</ListItemText>
        </MenuItem>
        {enableDeleteMode && (
          <MenuItem onClick={() => handleDelete(data?.fileName ?? '')}>
            <ListItemIcon>
              <img src={IconTrash} alt='' className='w-24' style={{ filter: 'brightness(0.9) contrast(1.9)' }} />
            </ListItemIcon>
            <ListItemText>Delete File</ListItemText>
          </MenuItem>
        )}
      </StyledMenu>
    </>
  );
};
